import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { Loader, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import * as Icons from "lucide-react";
import { toast } from "sonner";

const TYPES = ["video", "text", "image", "audio", "mcq", "quiz", "link", "completion", "story", "parent"];
const empty = { name: "", number: 1, week: 1, instructions: "", tips: "", deadline: "", points: 100, submission_type: "video", difficulty: "easy", mandatory: true, published: true, badge_id: null };

export default function QuestsManage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const { data, isLoading } = useQuery({ queryKey: ["all-quests"], queryFn: async () => (await api.get("/quests")).data });
  const { data: badges } = useQuery({ queryKey: ["badges"], queryFn: async () => (await api.get("/badges")).data });

  const save = async () => {
    if (!form.name) return toast.error("Quest name required");
    const payload = { ...form, number: Number(form.number), week: Number(form.week), points: Number(form.points), badge_id: form.badge_id === "none" ? null : form.badge_id };
    try {
      if (editId) await api.put(`/quests/${editId}`, payload); else await api.post("/quests", payload);
      toast.success("Quest saved!"); setOpen(false); setForm(empty); setEditId(null);
      qc.invalidateQueries({ queryKey: ["all-quests"] });
    } catch (e) { toast.error(apiErr(e)); }
  };
  const edit = (q) => { setForm({ ...q, badge_id: q.badge_id || "none" }); setEditId(q.id); setOpen(true); };
  const dup = async (id) => { await api.post(`/quests/${id}/duplicate`); qc.invalidateQueries({ queryKey: ["all-quests"] }); toast.success("Duplicated"); };
  const del = async (id) => { await api.delete(`/quests/${id}`); qc.invalidateQueries({ queryKey: ["all-quests"] }); toast.success("Deleted"); };
  const togglePub = async (q) => { await api.put(`/quests/${q.id}`, { ...q, published: !q.published, badge_id: q.badge_id }); qc.invalidateQueries({ queryKey: ["all-quests"] }); };

  if (isLoading) return <Loader />;

  return (
    <div className="space-y-5" data-testid="quests-manage">
      <div className="flex items-center justify-between">
        <h2 className="font-head text-3xl text-csh-blue">Quests 🎯</h2>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(empty); setEditId(null); } }}>
          <DialogTrigger asChild><Button className="rounded-full gradient-purple text-white" data-testid="add-quest-btn"><Icons.Plus size={18} /> New Quest</Button></DialogTrigger>
          <DialogContent className="rounded-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-head text-2xl text-csh-blue">{editId ? "Edit" : "Create"} Quest</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input placeholder="Quest name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl border-2 py-6" data-testid="quest-name" />
              <div className="grid grid-cols-3 gap-3">
                <Input type="number" placeholder="Number" value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} className="rounded-xl border-2 py-6" />
                <Input type="number" placeholder="Week" value={form.week} onChange={(e) => setForm({ ...form, week: e.target.value })} className="rounded-xl border-2 py-6" />
                <Input type="number" placeholder="Points" value={form.points} onChange={(e) => setForm({ ...form, points: e.target.value })} className="rounded-xl border-2 py-6" />
              </div>
              <Textarea placeholder="Instructions" value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} className="rounded-xl border-2" />
              <Input placeholder="Tips" value={form.tips} onChange={(e) => setForm({ ...form, tips: e.target.value })} className="rounded-xl border-2 py-6" />
              <div className="grid grid-cols-2 gap-3">
                <Select value={form.submission_type} onValueChange={(v) => setForm({ ...form, submission_type: v })}>
                  <SelectTrigger className="rounded-xl border-2 py-6" data-testid="quest-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={form.difficulty} onValueChange={(v) => setForm({ ...form, difficulty: v })}>
                  <SelectTrigger className="rounded-xl border-2 py-6"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="easy">Easy</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="hard">Hard</SelectItem></SelectContent>
                </Select>
              </div>
              <Select value={form.badge_id || "none"} onValueChange={(v) => setForm({ ...form, badge_id: v })}>
                <SelectTrigger className="rounded-xl border-2 py-6"><SelectValue placeholder="Reward badge" /></SelectTrigger>
                <SelectContent><SelectItem value="none">No badge</SelectItem>{(badges || []).map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
              <Input type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} className="rounded-xl border-2 py-6" />
              <div className="flex gap-6">
                <label className="flex items-center gap-2 text-sm font-bold"><Switch checked={form.mandatory} onCheckedChange={(v) => setForm({ ...form, mandatory: v })} /> Mandatory</label>
                <label className="flex items-center gap-2 text-sm font-bold"><Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} /> Published</label>
              </div>
              <Button onClick={save} className="w-full rounded-full gradient-orange py-6 font-bold text-white" data-testid="save-quest-btn">Save Quest</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {(!data || data.length === 0) ? <EmptyState icon="Target" title="No quests" /> : (
        <div className="grid gap-3 md:grid-cols-2">
          {data.map((q) => (
            <div key={q.id} className="rounded-3xl bg-white p-5 shadow-sm border border-black/5" data-testid={`mquest-${q.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs font-bold uppercase text-muted-foreground">Quest #{q.number} · Week {q.week} · {q.submission_type}</div>
                  <h3 className="font-head text-xl text-csh-blue">{q.name}</h3>
                  <div className="mt-1 flex gap-3 text-xs font-bold">
                    <span className="text-csh-orange">⭐ {q.points}</span>
                    <span className="text-yellow-600">{q.pending_count} pending</span>
                    <span className="text-green-600">{q.submission_count} submissions</span>
                  </div>
                </div>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${q.published ? "bg-green-100 text-green-700" : "bg-black/10 text-csh-grey"}`}>{q.published ? "LIVE" : "DRAFT"}</span>
              </div>
              <div className="mt-3 flex gap-1.5">
                <button onClick={() => edit(q)} className="rounded-lg p-2 hover:bg-black/5" data-testid={`edit-quest-${q.id}`}><Icons.Pencil size={16} className="text-csh-purple" /></button>
                <button onClick={() => dup(q.id)} className="rounded-lg p-2 hover:bg-black/5"><Icons.Copy size={16} className="text-csh-blue" /></button>
                <button onClick={() => togglePub(q)} className="rounded-lg p-2 hover:bg-black/5">{q.published ? <Icons.EyeOff size={16} /> : <Icons.Eye size={16} />}</button>
                <button onClick={() => del(q.id)} className="rounded-lg p-2 hover:bg-black/5"><Icons.Trash2 size={16} className="text-red-500" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
